import { parseInventoryQuantity } from './inventoryScannerModel.js';
import {
  INVENTORY_MODE_SET_ID,
  PHYSICAL_INVENTORY_TARGETS,
  activeUnitRecords,
  countInventoryUnits,
  inventoryCatalogForTarget,
} from './inventoryUnitModel.js';

export const PROGRAM_SET_NUMBERS = [1, 2, 3, 4, 5, 6];
const SEASON_STATUS_CATALOG_PREFIX = 'SEASON-';

function pad(number) {
  return String(Number(number) || 0).padStart(2, '0');
}

function safeId(value) {
  return String(value || '').replace(/[^A-Za-z0-9_.:-]+/g, '-');
}

export function programSetId(number) {
  return `SET-${pad(number)}`;
}

function seasonStatusCatalogId(number) {
  return `${SEASON_STATUS_CATALOG_PREFIX}${programSetId(number)}`;
}

export function programSetName(number) {
  return `Line Medic Set ${pad(number)}`;
}

export function programSetTargetIds(number) {
  const suffix = pad(number);
  return [`LM-PACK-${suffix}`, `TRAUMA-BAG-${suffix}`, `O2-BAG-${suffix}`];
}

export function preseasonAedTargetIds(setCount) {
  const requested = Math.max(0, Number(setCount) || 0);
  return PHYSICAL_INVENTORY_TARGETS
    .filter(target => target.type === 'aed' && !target.personal)
    .slice(0, requested)
    .map(target => target.id);
}

export function isInventorySeasonStatusRecord(record) {
  return String(record?.setId || '').trim().toUpperCase() === INVENTORY_MODE_SET_ID &&
    String(record?.catalogId || '').trim().toUpperCase().startsWith(SEASON_STATUS_CATALOG_PREFIX);
}

function statusFromNotes(notes) {
  const match = /season\s+status\s*:\s*(active|oos|out\s+of\s+service)/i.exec(String(notes || ''));
  if (!match) return '';
  return /^active$/i.test(match[1]) ? 'active' : 'oos';
}

export function programSetStatus(expirations = [], number) {
  const catalogId = seasonStatusCatalogId(number);
  const marker = [...(Array.isArray(expirations) ? expirations : [])]
    .reverse()
    .find(record => isInventorySeasonStatusRecord(record) && String(record?.catalogId || '').trim().toUpperCase() === catalogId);
  return statusFromNotes(marker?.notes) || (Number(number) <= 3 ? 'active' : 'oos');
}

export function programSetIsActive(expirations = [], number) {
  return programSetStatus(expirations, number) === 'active';
}

export function programSetNumberForTarget(targetId) {
  const match = /^(?:LM-PACK|TRAUMA-BAG|O2-BAG)-(\d{2})$/i.exec(String(targetId || '').trim());
  return match ? Number(match[1]) : 0;
}

export function inventoryTargetSeasonStatus(expirations = [], target) {
  const number = programSetNumberForTarget(target?.id);
  if (number) return programSetStatus(expirations, number);
  return String(target?.seasonalStatus || 'active').trim().toLowerCase() === 'oos' ? 'oos' : 'active';
}

export function makeProgramSetStatusRecord(number, active) {
  const catalogId = seasonStatusCatalogId(number);
  return {
    recordId: safeId(`${INVENTORY_MODE_SET_ID}-${catalogId}`),
    setId: INVENTORY_MODE_SET_ID,
    setName: 'Inventory Season Status',
    catalogId,
    component: 'Program Set',
    item: programSetName(number),
    requiredQty: '0',
    expirationDate: '',
    expirationState: 'NA',
    lotSerial: '',
    returnedToLogistics: true,
    returnedDate: '',
    replacementDate: '',
    notes: `Season status: ${active ? 'active' : 'oos'}`,
    updatedAt: '',
  };
}

function targetMeta(targetId) {
  return PHYSICAL_INVENTORY_TARGETS.find(target => target.id === targetId) || { id: targetId, name: targetId };
}

function catalogRequirement(item) {
  const raw = String(item?.requiredQty ?? '').trim();
  const quantity = parseInventoryQuantity(raw, 0);
  return {
    quantity: Math.max(0, quantity),
    resolved: Boolean(raw && Number.isFinite(quantity) && quantity > 0),
  };
}

function itemType(item) {
  const notes = String(item?.notes || '');
  const match = /item\s+type\s*:\s*([^|]+)/i.exec(notes);
  return String(item?.itemType || match?.[1] || '').trim();
}

export function buildPreseasonPlan(catalog = [], expirations = []) {
  const activeSets = PROGRAM_SET_NUMBERS.filter(number => programSetIsActive(expirations, number));
  const targetPlans = [];
  const aggregate = new Map();
  const unresolved = [];

  const addTargetPlan = (targetId, setNumber = 0) => {
    const target = targetMeta(targetId);
    const scoped = inventoryCatalogForTarget(catalog, targetId);
    const rows = scoped.map(item => {
      const requirement = catalogRequirement(item);
      const records = activeUnitRecords(expirations, targetId, item.catalogId);
      const onHand = countInventoryUnits(records);
      const needed = requirement.resolved ? Math.max(0, requirement.quantity - onHand) : 0;
      const row = {
        targetId,
        targetName: target.name,
        setNumber,
        catalogId: String(item?.catalogId || '').trim(),
        item: String(item?.item || '').trim(),
        component: String(item?.component || '').trim(),
        itemType: itemType(item),
        required: requirement.quantity,
        requiredLabel: String(item?.requiredQty || '').trim(),
        standardResolved: requirement.resolved,
        onHand,
        needed,
      };
      if (!requirement.resolved) unresolved.push(row);

      if (requirement.resolved) {
        const key = row.catalogId;
        const current = aggregate.get(key) || {
          catalogId: row.catalogId,
          item: row.item,
          component: row.component,
          itemType: row.itemType,
          required: 0,
          onHand: 0,
          needed: 0,
          targets: 0,
        };
        current.required += row.required;
        current.onHand += Math.min(row.onHand, row.required);
        current.needed += row.needed;
        current.targets += 1;
        aggregate.set(key, current);
      }
      return row;
    });

    targetPlans.push({
      ...target,
      setNumber,
      rows,
      neededUnits: rows.reduce((sum, row) => sum + row.needed, 0),
      unresolved: rows.filter(row => !row.standardResolved),
    });
  };

  activeSets.forEach(number => {
    programSetTargetIds(number).forEach(targetId => addTargetPlan(targetId, number));
  });

  // AEDs are a shared program pool rather than permanently tied to numbered sets.
  // Preseason planning prepares one AED for every set being put in service, using
  // the first available program AEDs. This keeps the order total based on how many
  // complete deployment sets are needed rather than which set numbers are active.
  preseasonAedTargetIds(activeSets.length).forEach((targetId, index) => {
    addTargetPlan(targetId, activeSets[index] || 0);
  });

  const orderRows = [...aggregate.values()]
    .filter(row => row.needed > 0)
    .sort((a, b) => a.component.localeCompare(b.component) || a.item.localeCompare(b.item));

  return {
    activeSets,
    targetPlans,
    orderRows,
    unresolved,
    totalOrderUnits: orderRows.reduce((sum, row) => sum + row.needed, 0),
    totalRequiredUnits: [...aggregate.values()].reduce((sum, row) => sum + row.required, 0),
    totalOnHandUnits: [...aggregate.values()].reduce((sum, row) => sum + row.onHand, 0),
  };
}

export function targetBuildTicket(plan, targetId) {
  const target = (plan?.targetPlans || []).find(item => item.id === targetId);
  if (!target) return null;
  return {
    ...target,
    rows: target.rows.filter(row => row.needed > 0 || !row.standardResolved),
  };
}

export function preseasonOrderCsvRows(plan, orderQuantities = {}) {
  return (plan?.orderRows || []).map(row => ({
    'Catalog ID': row.catalogId,
    Component: row.component,
    Item: row.item,
    'Item Type': row.itemType,
    'Program Required': row.required,
    'Good Stock Assigned': row.onHand,
    'System Need': row.needed,
    'Order Qty': Math.max(0, Number(orderQuantities?.[row.catalogId] ?? row.needed) || 0),
  }));
}
